import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router'

import { useAccess } from '@/shared/access'
import { useFormat } from '@/shared/i18n/use-format'
import { Skeleton } from '@/shared/layout/Skeleton'
import { cn } from '@/shared/ui'
import { inlineLink } from '@/shared/ui/cn'

import { useActorNames, type Actor, type ActorNameState } from './actor-names'
import { createdBy, modifiedBy, type Audit } from './audit'

/** The mockups' "Created: Friday, July 30, 2021 1:53 PM (Central Standard Time)". */
const stampFormat: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'long',
}

const typeLabels = {
  user: 'details.actor.user',
  apiClient: 'details.actor.apiClient',
  system: 'details.actor.system',
} as const

/**
 * A details page's "Created: {when} by {who}" and "Modified: …" lines, as the mockups' Course and
 * Survey Details end. Each actor is named by its type's lookup (`ActorNamesProvider`), one call
 * per type; a system actor is "System", and a type with no lookup shows as the type alone. A list
 * passes `nameOf` from one `useActorNames` over all its records, so it looks up once, not per item.
 */
export function CreatedModified(props: {
  readonly audit: Audit
  readonly className?: string
  readonly nameOf?: (actor: Actor) => ActorNameState
}) {
  const intl = useIntl()
  const format = useFormat()
  const { audit } = props
  const actors = useMemo(() => (props.nameOf === undefined ? [createdBy(audit), modifiedBy(audit)] : []), [audit, props.nameOf])
  const ownNameOf = useActorNames(actors)
  const nameOf = props.nameOf ?? ownNameOf

  const line = (id: string, on: string, actor: Actor) =>
    intl.formatMessage(
      { id },
      {
        date: format.dateTime(on, stampFormat),
        actor: <ActorLabel key="actor" state={nameOf(actor)} typeLabel={intl.formatMessage({ id: typeLabels[actor.type] })} />,
      },
    )

  return (
    <div className={cn('border-t border-divider pt-4 text-xs text-text-light', props.className)}>
      <p>{line('details.audit.created', audit.createdOn, createdBy(audit))}</p>
      <p>{line('details.audit.modified', audit.modifiedOn, modifiedBy(audit))}</p>
    </div>
  )
}

function ActorLabel(props: { readonly state: ActorNameState; readonly typeLabel: string }) {
  const intl = useIntl()
  const access = useAccess()
  const { state } = props
  if (state === undefined) {
    return (
      <span className="inline-flex align-middle">
        <span className="sr-only">{intl.formatMessage({ id: 'details.actor.loading' })}</span>
        <Skeleton className="h-3 w-24" />
      </span>
    )
  }
  if (state === null) {
    return <>{props.typeLabel}</>
  }
  return state.path === undefined || (state.permission !== undefined && !access.can(state.permission)) ? (
    <>{state.name}</>
  ) : (
    <Link to={state.path} className={inlineLink}>
      {state.name}
    </Link>
  )
}
